import { Card, CardContent } from "@/components/ui/card";
import { DollarSign, TrendingUp, Clock, AlertTriangle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

type KPIs = {
  totalInvoiced: number;
  totalCollected: number;
  outstanding: number;
  overdueCount: number;
  pendingChargesTotal?: number;
};

type Props = {
  kpis: KPIs | undefined;
  isLoading: boolean;
};

function fmt(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function InvoiceKpiCards({ kpis, isLoading }: Props) {
  const cards = [
    {
      title: "Total Invoiced",
      value: kpis ? fmt(kpis.totalInvoiced) : "—",
      icon: DollarSign,
      bg: "bg-blue-100 dark:bg-blue-900/30",
      color: "text-blue-600 dark:text-blue-400",
    },
    {
      title: "Collected",
      value: kpis ? fmt(kpis.totalCollected) : "—",
      icon: TrendingUp,
      bg: "bg-green-100 dark:bg-green-900/30",
      color: "text-green-600 dark:text-green-400",
    },
    {
      title: "Total Owed",
      value: kpis ? fmt(kpis.outstanding + (kpis.pendingChargesTotal ?? 0)) : "—",
      icon: Clock,
      bg: "bg-amber-100 dark:bg-amber-900/30",
      color: "text-amber-600 dark:text-amber-400",
    },
    {
      title: "Overdue (30+ days)",
      value: kpis ? String(kpis.overdueCount) : "—",
      icon: AlertTriangle,
      bg: "bg-red-100 dark:bg-red-900/30",
      color: "text-red-600 dark:text-red-400",
    },
  ];

  return (
    <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
      {cards.map(card => (
        <Card key={card.title}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`p-2 rounded-full ${card.bg}`}>
              <card.icon className={`h-5 w-5 ${card.color}`} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{card.title}</p>
              {isLoading ? (
                <Skeleton className="h-6 w-20 mt-1" />
              ) : (
                <p className="text-lg font-semibold">{card.value}</p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
